import type { BandejaAutorizacionItem } from "@/lib/types";
import {
  etiquetaTipoSolicitud,
  etiquetaSubtipoSolicitud,
} from "@/lib/solicitud-labels";
import { formatearMonto } from "@/shared/format/money";
import { formatFechaHoraIso } from "@/shared/format/fecha";

export type BandejaSortKey =
  | "folio"
  | "tipo"
  | "organizacion"
  | "area"
  | "proveedor"
  | "numProveedor"
  | "rfcEmisor"
  | "descripcion"
  | "total"
  | "moneda"
  | "nivel"
  | "fechaCreacion"
  | "fecha";

export type SortDir = "asc" | "desc";

export interface BandejaSort {
  key: BandejaSortKey;
  dir: SortDir;
}

/** Comparación ascendente por la columna dada · nulos al final. */
function comparar(a: BandejaAutorizacionItem, b: BandejaAutorizacionItem, key: BandejaSortKey): number {
  switch (key) {
    case "folio":
      return a.numeroSolicitud - b.numeroSolicitud;
    case "tipo":
      return (
        (a.tipo ?? "").localeCompare(b.tipo ?? "", "es") ||
        (a.subtipo ?? "").localeCompare(b.subtipo ?? "", "es")
      );
    case "organizacion":
      return (a.organizacionNombre ?? "").localeCompare(b.organizacionNombre ?? "", "es");
    case "area":
      return (a.areaNombre ?? "").localeCompare(b.areaNombre ?? "", "es");
    case "proveedor":
      return (a.proveedorRazonSocial ?? "").localeCompare(b.proveedorRazonSocial ?? "", "es");
    case "numProveedor":
      return (a.proveedorNumero ?? Number.NEGATIVE_INFINITY) - (b.proveedorNumero ?? Number.NEGATIVE_INFINITY);
    case "rfcEmisor":
      return (a.rfcEmisor ?? "").localeCompare(b.rfcEmisor ?? "", "es");
    case "descripcion":
      return (a.descripcionPrimerConcepto ?? "").localeCompare(b.descripcionPrimerConcepto ?? "", "es");
    case "total":
      return (a.totalCfdi ?? Number.NEGATIVE_INFINITY) - (b.totalCfdi ?? Number.NEGATIVE_INFINITY);
    case "moneda":
      return (a.monedaCfdi ?? "").localeCompare(b.monedaCfdi ?? "", "es");
    case "nivel":
      return a.nivelActual - b.nivelActual;
    case "fechaCreacion":
      return new Date(a.fechaCreacion).getTime() - new Date(b.fechaCreacion).getTime();
    case "fecha":
      return new Date(a.fechaUltimoEvento).getTime() - new Date(b.fechaUltimoEvento).getTime();
    default:
      return 0;
  }
}

/** Devuelve una copia ordenada · si `sort` es null conserva el orden original. */
export function ordenarBandeja(
  items: BandejaAutorizacionItem[],
  sort: BandejaSort | null,
): BandejaAutorizacionItem[] {
  if (!sort) return items;
  const factor = sort.dir === "asc" ? 1 : -1;
  return [...items].sort((a, b) => factor * comparar(a, b, sort.key));
}

/** Estado siguiente al hacer click en una columna: nueva → asc; misma → alterna asc/desc. */
export function siguienteSort(actual: BandejaSort | null, key: BandejaSortKey): BandejaSort {
  if (actual?.key === key) {
    return { key, dir: actual.dir === "asc" ? "desc" : "asc" };
  }
  return { key, dir: "asc" };
}

// --- Filtro por columna ------------------------------------------------------

export type BandejaFiltros = Partial<Record<BandejaSortKey, string>>;

/** Texto buscable de una celda para el filtro por columna (case-insensitive contains). */
export function textoColumna(it: BandejaAutorizacionItem, key: BandejaSortKey): string {
  switch (key) {
    case "folio":
      return it.folio ?? "";
    case "tipo":
      return `${etiquetaTipoSolicitud(it.tipo)} ${etiquetaSubtipoSolicitud(it.subtipo)}`;
    case "organizacion":
      return it.organizacionNombre ?? "";
    case "area":
      return it.areaNombre ?? "";
    case "proveedor":
      return it.proveedorRazonSocial ?? "";
    case "numProveedor":
      return it.proveedorNumero !== null ? String(it.proveedorNumero) : "";
    case "rfcEmisor":
      return it.rfcEmisor ?? "";
    case "descripcion":
      return it.descripcionPrimerConcepto ?? "";
    case "total":
      // Número con 2 decimales sin separadores (consistente con el server: ToString("0.00")).
      return it.totalCfdi !== null ? it.totalCfdi.toFixed(2) : "";
    case "moneda":
      return it.monedaCfdi ?? "";
    case "nivel":
      // Niveles YA autorizados / total (el nivel actual aún no se autoriza).
      return `${it.nivelActual - 1}/${it.cantidadNiveles}`;
    case "fechaCreacion":
      return formatFechaHoraIso(it.fechaCreacion);
    case "fecha":
      return formatFechaHoraIso(it.fechaUltimoEvento);
    default:
      return "";
  }
}

/** Normaliza para un contains robusto: minúsculas + sin acentos + solo alfanuméricos.
 *  Debe coincidir con la normalización del backend (BandejaAutorizacionQuery.Normalizar). */
export function normalizarBusqueda(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9]/g, "");
}

/** Filtra por todas las columnas con texto (AND, contains normalizado). */
export function filtrarBandeja(
  items: BandejaAutorizacionItem[],
  filtros: BandejaFiltros,
): BandejaAutorizacionItem[] {
  const activos = Object.entries(filtros)
    .map(([k, v]) => [k as BandejaSortKey, normalizarBusqueda(v ?? "")] as const)
    .filter(([, v]) => v.length > 0);
  if (activos.length === 0) return items;
  return items.filter((it) =>
    activos.every(([key, texto]) => normalizarBusqueda(textoColumna(it, key)).includes(texto)),
  );
}

/** Filtros con al menos un valor no vacío → objeto listo para query params (server-side). */
export function filtrosNoVacios(filtros: BandejaFiltros): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(filtros)) {
    if (v && v.trim().length > 0) out[k] = v.trim();
  }
  return out;
}
